import dopamineJournalImg from "../../assets/journaling/dopamine-journal.png";
import wheelOfLifeImg from '../../assets/journaling/wheel-of-life.png';
import wheelOfLifeSheetImg from '../../assets/journaling/wheel-of-life-sheet.png';
import timeBlockingImg from '../../assets/journaling/time-blocking.png';
import unsentLetters from '../../assets/journaling/unsent-letters.png';
const journalTypesData = {
 free: {
    icon: "✍️",
    title: "Free Journaling",
    subtitle: "Write without rules. Let your thoughts flow naturally.",
    accent: "#5b7fd9",
    accentSoft: "#f2f7ff",
    quote: "Thoughts left unwritten can turn into poison.",
    sections: [
      {
        heading: "Why Most People Don't Journal",
        items: [
          {
            text: "Most people don't journal for one specific reason:\nthey think it has to be structured — no mistakes, perfect grammar, perfect language.\nThey think it has to be perfect.\n\nBut journaling isn't about that at all.",
          },
        ],
      },
      {
        heading: "What It Actually Is",
        items: [
          {
            text: "It's about writing freely.\nYou can mix languages, draw, switch topics that don't even make sense.\n\nThis isn't an article. This isn't a book.\nThis is just you, sitting with your thoughts, letting them get out of your mind.",
          },
        ],
      },
      {
        heading: "Why Letting Them Out Matters",
        items: [
          {
            text: "Because sometimes, holding them in for too long can turn into poison.\nSo instead, we let them out — freely, in whatever way they want to go.",
          },
        ],
      },
      {
        heading: "The Basics",
        items: [
          {
            text: "This is the most basic form of journaling.\nYou can practice it anywhere, anytime — just write whatever comes.\nNo rules here.",
          },
        ],
      },
    ],
    prompts: [
      "What's on my mind right now, exactly as it is?",
      "What am I avoiding writing about?",
      "If no one ever read this, what would I say?",
    ],
  },

prompted: {
    icon: "🧭",
    title: "Prompted Journaling",
    subtitle: "Guided questions to help you reflect with intention.",
    accent: "#c99a2e",
    accentSoft: "#fff2cc",
    quote: "Understanding yourself starts with asking the right questions.",
    sections: [
      {
        heading: "What Prompted Journaling Is",
        items: [
          {
            text: "Prompted journaling is a tool for understanding yourself better — either overall, or in one specific area of your life.\n\nAs I mentioned in the Planning journal, we can split what matters into Life and Work. Life breaks down into relationships (friends, family, romance) and health (mind, body, soul). Work breaks down into career, growth, mission, and money. You could add satisfaction, or any number of other areas — it really depends on what you're looking to explore.\n\nUnderstanding yourself means digging deeper, by asking yourself the right questions.",
          },
        ],
      },
      {
        heading: "Where the Questions Come From",
        items: [
          {
            text: "You can ask yourself the right questions — or borrow them from someone who already has.\nThere are ready-made prompt journals built for daily use, and others that focus on a specific area of life.\nYou'll also find prompts inside personal development books, meant to be answered alongside reading.",
          },
        ],
      },
      {
        heading: "When It's Most Useful",
        items: [
          {
            text: "Prompted journaling is especially useful when you're going through a messy phase of life and want to put every piece back in its place.\nIt's perfect for new chapters — a new year, a new marriage, starting a new school.\n\nThis type of journaling lets you focus on one area at a time, which makes it easier to find answers and gain clarity.",
          },
        ],
      },
      {
        heading: "Getting Started",
        items: [
          {
            text: "If you're new to the habit, a ready-made prompt journal is the easiest way in — the structure is already there, all you have to do is respond.\nOnce you're more familiar with it, you can build your own by intentionally choosing the questions you want to reflect on.",
          },
        ],
      },
      {
        heading: "A Few Recommendations",
        items: [
          {
            subheading: "Books with Prompts",
            text: "A few I'd recommend, to answer alongside reading:",
            links: [
              { label: "The Answer — Allan & Barbara Pease", url: "https://www.amazon.com/Answer-take-charge-become-person/dp/1409168298" },
              { label: "Don't F*cking Panic — Kelsey Darragh", url: "https://www.amazon.com/Dont-Fucking-Panic-Kelsey-Darragh/dp/B08NTZHH2X" },
              { label: "Change Your Life with NLP — Lindsey Agness", url: "https://www.amazon.com/Change-Your-Life-NLP-Powerful/dp/0273716980" },
              { label: "Before We Get Older — my own book", url: "/before-we-get-older", internal: true },
            ],
          },
          {
            subheading: "Journals I'd Suggest",
            text: "A couple of ready-made prompt journals worth checking out:",
            links: [
              { label: "Daily Deep Journal", url: "https://daily-deep.com/" },
              { label: "The Five Minute Journal", url: "https://www.intelligentchange.com/products/the-five-minute-journal" },
            ],
          },
        ],
      },
    ],
    prompts: [
      "What's one thing I'm proud of this week?",
      "What would make today feel complete?",
      "What's a belief I've outgrown?",
    ],
  },

  diary: {
    icon: "📖",
    title: "Diary",
    subtitle: "Capture moments, emotions, and everyday memories.",
    accent: "#9b6fd1",
    accentSoft: "#f7f0ff",
    quote: "You are the hero of your own story.",
    sections: [
      {
        heading: "Your Story, No Rules",
        items: [
          {
            text: "I consider your diary your story — and you're the hero in it.\n\nWhenever you feel like writing, there's no pressure, no need to do it daily.\nGrab a pen and write about what happened today, yesterday, or ten days ago — whenever.\nJust write it down knowing no one will read it. Because no one will.\n\nThat's the magic of a diary journal.",
          },
        ],
      },
      {
        heading: "Why It Works",
        items: [
          {
            subheading: "Talking to Yourself",
            text: "You write as if you're talking to yourself — and you can't lie to yourself.\nSo you're in your most authentic state in that moment.",
          },
          {
            subheading: "No Fear of Judgment",
            text: "With friends, family, even a therapist, we tend to hide parts of ourselves — afraid of being judged. And sometimes, we are.\nNo one can fully understand your perspective but you. If someone hasn't been through what you've been through, how could they?\n\nWriting without fear of judgment lets you express everything fully, exactly as it is.",
          },
        ],
      },
      {
        heading: "Releasing What You Feel",
        items: [
          {
            text: "It's a way to release emotions — the hard ones, and the happy ones too.\nEmotions need to be released. If they're not, they find a place to sit in the body, sometimes even as illness. You never know.\n\nSo make sure that whatever happens, you release it — by writing it down freely.\nSometimes it takes more than once until it fades. Trust me, it will.",
          },
        ],
      },
      {
        heading: "Rereading Your Diary",
        items: [
          {
            text: "Then time passes, and one day you pick up your old diary and read it — six months later, a year later, whenever.\n\nEvery time I do that, I laugh so hard. There were things I thought I'd never get over, and I did.\nRereading brings back forgotten memories, gives you confidence that everything passes, and reminds you how much you've grown since that moment.\nIt does a lot, honestly.",
          },
        ],
      },
      {
        heading: "Your Collection",
        items: [
          {
            text: "That's why I think keeping a diary is so valuable in the long run.\nAfter a few years, you'll have a shelf of journals — and collecting them feels special, because this is your story. Your movie.",
          },
        ],
      },
    ],
    prompts: [
      "What happened today that I want to remember?",
      "Who did I talk to, and how did it feel?",
      "What made me smile today?",
    ],
  },
  dopamine: {
    icon: "⚡",
    title: "Dopamine",
    subtitle: "Short positive entries to boost motivation and mood.",
    accent: "#d9645b",
    accentSoft: "#ffeaea",
    quote: "We're not living the most miserable life — we're just not paying attention to the little, precious, magical moments.",
    sections: [
      {
        heading: "Made for Beginners",
        items: [
          {
            text: "This journal is for beginners.\nFor those who feel like writing is too much.\nYes, maybe you can't write a whole page — I agree.\nBut you can definitely write one sentence.\nAnd that's what dopamine journaling is about.",
          },
        ],
      },
      {
        heading: "How It Works",
        items: [
          {
            subheading: "One Sentence a Day",
            image: dopamineJournalImg,
            text: "You take a journal, like the one in the picture.\n\nFor each month, you write one sentence about what happened that day.",
          },
          {
            subheading: "Monthly Adventure",
            text: "There's also a section for this month's adventure — something you plan at the start of the month.\nMake sure the month doesn't pass by without you experiencing something close to your heart.\nThis adventure can be big, like going on a trip,\nor small, like meeting a friend for coffee, or going for a run…\n\nAnd a section for the outcome, where you write what actually happened by the end of the month.",
          },
          {
            subheading: "Monthly Reflections",
            text: "A section where you write 3 things you learned this month.",
          },
          {
            subheading: "The Score",
            text: "You give a score for each of these areas:\nHealth\nWealth\nLearning\nRelationships\nSatisfaction",
          },
        ],
      },
      {
        heading: "The Highlight Trick",
        items: [
          {
            text: "One more thing, at the end of the month: you take 2 highlighters.\nI use green and pink —\ngreen to highlight 2 great days I had,\npink to highlight days that weren't really good.\n\nThis rewires your brain to see that not all your days are as bad as you think,\nbecause most of the time we believe we're living the most miserable life.\nIn fact, we're not — we're just not paying attention to the little, precious, magical moments.",
          },
        ],
      },
    ],
    prompts: [
      "One small win from today.",
      "Something that made me laugh.",
      "A reason I'm looking forward to tomorrow.",
    ],
  },

  planning: {
    icon: "🗂",
    title: "Planning",
    subtitle: "Organize your goals, tasks, and intentions.",
    accent: "#3f9d6e",
    accentSoft: "#eafaf1",
    quote: "A goal is just a goal until you write it down.",
    sections: [
      {
        heading: "Getting Started",
        items: [
          {
            text: "Note: this is just how I personally plan — adapt it to whatever works for you.\n\nA goal is just a goal until you write it down.\nThis journal is about turning your ideas into something concrete.",
          },
        ],
      },
      {
        heading: "Why Planning Matters",
        items: [
          {
            text: "Planning gives you a clear picture of what you want to do — written down, not just floating in your head.\nMost of the time, we don't act because we don't know where to start.\nWriting it down gives you that starting point, which makes everything after it easier.\n\nThe real question is what to plan, and how — and that starts with asking the right questions.",
          },
        ],
      },
      {
        heading: "The Method",
        items: [
          {
            text: "It comes down to three steps: Reflect, Plan, Track —\napplied across two areas of your life: Life and Work.",
          },
        ],
      },
      {
        heading: "Reflect",
        items: [
          {
            subheading: "Where You Are Right Now",
            text: "Before you plan, you need to know where you stand, so you know where you're heading.\nThat's what reflection is for — asking what's going well, and what isn't.",
          },
          {
            subheading: "Reflecting on Life",
            text: "This covers your relationships — friends and family — and your health: body, mind, soul.",
          },
          {
            subheading: "Reflecting on Work",
            text: "Give yourself an honest review of your career, your finances, and how aligned you are with your goals — and where there's room to improve.",
          },
          {
            subheading: "A Visual Method: Wheel of Life",
            image: wheelOfLifeImg,
            text: "The Wheel of Life is a simple way to score each area of your life — plus one more: joy.\nBecause scoring a 10 everywhere doesn't mean much if you're not actually enjoying it.\n\nYou can draw it as a wheel, or track it in a spreadsheet instead, like the one below.",
          },
          {
            subheading: "Wheel of Life — Spreadsheet Version",
            image: wheelOfLifeSheetImg,
            text: "If drawing isn't your thing, a simple spreadsheet works just as well — score each area, and you'll instantly see where the gaps are.",
          },
          {
            subheading: "How Often to Reflect",
            text: "Once a month works well — or once a quest, every 3 months.",
          },
        ],
      },
      {
        heading: "Plan",
        items: [
          {
            subheading: "Get Clear on Your Goals",
            text: "Once you know where you stand, ask yourself: what are your goals?\nThen prioritize them — realistically, you can't be a 10 in every area.\nIf health is your priority this quest, you'll naturally make more time for the gym and meal prep.",
          },
          {
            subheading: "Keep the Joy Factor",
            text: "For each goal, ask: how can I make this 5% more enjoyable?\nThat keeps the joy factor alive.",
          },
          {
            subheading: "Practice: The 5-Year Plan",
            text: "Imagine your life 5 years from now, and write it out in detail —\ncovering every area we just talked about.",
          },
          {
            subheading: "Practice: Time Blocking",
            image: timeBlockingImg,
            text: "Time blocking works because it turns a vague goal into a commitment —\nyou're not negotiating with yourself about whether to do something, because it's already scheduled.\n\nBlock your day into chunks, for example:\n7–9 gym, 10–2 deep work, 4–6 groceries, 6–8 family time —\nand assign each chunk a task.",
          },
        ],
      },
      {
        heading: "Track",
        items: [
          {
            text: "Keep the cycle going with a weekly check-in.\nWrite what went well, how you felt, how you reacted —\nand what didn't go well, why, and how to avoid repeating it.",
          },
        ],
      },
      {
        heading: "When to Plan: The Quest System",
        items: [
          {
            text: "So when do you actually plan — the whole year at once, or day by day?\nI use the Quest system: split the year into four 3-month quests\n(Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec), and plan one quest at a time.\n\nIt's a timeframe short enough to estimate realistically,\nand long enough to make real progress —\nand you get to adjust your priorities every time a new quest starts.\n\nGet yourself a planning journal to plan your life, track your goals, and stay on top of it all 🗓️",
          },
        ],
      },
    ],
    prompts: [
      "What's my one priority for tomorrow?",
      "What's blocking my progress this week?",
      "What can I let go of from my list?",
    ],
  },

  gratitude: {
    icon: "🌸",
    title: "Gratitude",
    subtitle: "Reflect on what you're thankful for, big or small.",
    accent: "#c78a3d",
    accentSoft: "#fff5e6",
    quote: "A healthy man has many wishes; a sick man has only one.",
    sections: [
      {
        heading: "Why We Need Gratitude Journaling",
        items: [
          {
            text: "We live in a society where complaining feels normal. You meet a friend, and you're almost expected to complain — to talk about what's going wrong in your life. And that can be beneficial in some way; it's a form of releasing negative emotions. But after noticing this pattern over and over, I realized we've been trained to focus on the negative side of things. That's exactly what makes it hard for our brains to shift toward the bright side. Gratitude journaling is one of the simplest ways out of that pattern.",
          },
        ],
      },
      {
        heading: "The Practice",
        items: [
          {
            subheading: "What Is Gratitude Journaling?",
            text: "A very simple practice: you write down what you're grateful for. All you have to write is three things — just three things every day will do so much for you.",
          },
          {
            subheading: "Morning Journaling",
            text: "Adding a gratitude journal to your morning routine rewires your brain to focus on the good things in your life, and you carry that energy with you through the day.",
          },
          {
            subheading: "Night Journaling",
            text: "Sit with yourself for a moment before you sleep and list what you were grateful for that day. If the day wasn't great, this practice will show you there were still good things — you just weren't paying attention to them.",
          },
        ],
      },
      {
        heading: "Why It Matters",
        items: [
          {
            subheading: "Why It Works",
            text: "Gratitude journaling is incredibly calming, and it helps you realize how much good is actually happening around you. Being alive every day means you still have a chance, you're still in the game. Being healthy, able to walk, breathe, and move without pain, might be the biggest thing you can be grateful for. Having food, a job, a friend, a phone — these are all things other people wish they had.",
          },
          {
            subheading: "If You're Going Through a Hard Time",
            text: "I remember a period in my life when I wasn't feeling great, and I started practicing gratitude every day. I was surprised by how much happiness started flowing through my days.",
          },
        ],
      },
      {
        heading: "Getting Started",
        items: [
          {
            text: "For beginners, I suggest buying a small notebook and practicing there — just three things you're grateful for, every day. You can add it as a prompt inside another journal, but I think it's better to keep it separate at first, to stay committed to the habit.",
          },
        ],
      },
      {
        heading: "Gratitude in Islam",
        items: [
          {
            text: "If you're Muslim, you probably say 'Alhamdulillah' regularly — but we don't always say it with intention. Try saying it while naming exactly what you're thanking God for. It gives the words more value, more power, more feeling. So look around today, and be grateful.",
          },
        ],
      },
    ],
    prompts: [
      "Three small things I'm grateful for today.",
      "Someone who made my day better, and why.",
      "A comfort I often take for granted.",
    ],
  },

  morning: {
    icon: "🌅",
    title: "Morning Journal",
    subtitle: "Clear your mind first thing, before the day takes over.",
    accent: "#e0893f",
    accentSoft: "#fff3e6",
    quote: "Start the day with a lighter, clearer mind.",
    sections: [
      {
        heading: "What It Is",
        items: [
          {
            text: "The morning journal is more like a morning dump — a way to clear out all the thoughts that are stuck in your head when you wake up.\nThe goal is to start the day with a lighter, clearer mind.",
          },
        ],
      },
      {
        heading: "How It Works",
        items: [
          {
            text: "I like to do this in the form of sentences, putting down on paper whatever I'm still thinking about — whether I'm worried, excited, or just processing something.\n\nAnd of course, this has to be done in the morning, before you dive into your day.",
          },
        ],
      },
    ],
    prompts: [
      "What's the first thing on my mind this morning?",
      "What do I want to let go of before the day starts?",
      "How do I want to feel today?",
    ],
  },

  affirmation: {
    icon: "✨",
    title: "Affirmation Journal",
    subtitle: "Small reminders of who you are and what you have.",
    accent: "#d4699e",
    accentSoft: "#fdeef4",
    quote: "Stand by your own side.",
    sections: [
      {
        heading: "What It Is",
        items: [
          {
            text: "An affirmation journal can be a petite (small) form of journal dedicated just to affirmations.\nI see this as more of a seasonal practice — something to turn to during times when you feel lower than usual.",
          },
        ],
      },
      {
        heading: "Why It Helps",
        items: [
          {
            text: "It's like standing by your own side, gently reminding yourself of all the beautiful things you are and all that you already have.\n\nWriting affirmations is also a way to choose the qualities of the version of yourself you want to embody today and tomorrow, and how you want to show up in the world.",
          },
        ],
      },
    ],
    prompts: [
      "An affirmation for who I want to be today.",
      "Something beautiful about myself I forget to notice.",
      "A quality I want to embody more.",
    ],
  },
 "unsent-letters":  {
    icon: "💌",
    title: "Unsent Letters",
    subtitle: "Say what you need to say — without ever sending it.",
    accent: "#4a7c8c",
    accentSoft: "#e8f4f6",
    quote: "Some things are meant to be written, never sent.",
    sections: [
      {
        heading: "Why We Need This",
        items: [
          {
            text: "Our relationship with ourselves matters — but so does our relationship with others.\nUnfortunately, sometimes we can't open up to certain people, for all kinds of reasons.\n\nMaybe they're not in your life anymore. Maybe they're not mature enough to understand you. Maybe opening up to them would only hurt you later.\nMaybe it's someone you love who doesn't love you back, someone who hurt you, someone you miss deeply — whoever it is.",
          },
        ],
      },
      {
        heading: "What This Journal Is For",
        items: [
          {
            text: "This journal is for writing to them — \"Dear…\" — without ever sending it.\nThe goal is to release the emotion: anger, longing, pain, desire, love, whatever it is.\nBecause telling them directly is often the wrong thing to do.",
          },
        ],
      },
      {
        heading: "How to Write It",
        items: [
          {
            image: unsentLetters,
            text: "Write the letter as if you're speaking to that person, face to face.\nSay whatever you want — things you'd probably never say in real life, to anyone.\nYou can even insult them. Who cares. The point is to release the emotion.\n\nYou can write it more than once if you need to.",
          },
        ],
      },
      {
        heading: "What to Do With It",
        items: [
          {
            text: "Once it's written, the letter is yours to do with as you like.\nYou can throw it away, burn it, or keep it.\nI personally love to keep mine.",
          },
        ],
      },
      {
        heading: "What Happens After",
        items: [
          {
            text: "Over time, you'll feel that pull — the desire to talk to this person — start to fade.\nThis helps you move on calmly and peacefully, without holding onto grudges or unspoken feelings.\nThey're released.",
          },
        ],
      },
      {
        heading: "An Example",
        items: [
          {
            text: "Dear You,\n\nI don't even know where to start, so I'll just start.\n\nI was angry for a long time. Then I missed you. Then I was angry again. I don't think I ever said any of that out loud, not really — I said the polite version, the version that didn't scare anyone off.\n\nThis is the other version.\n\nI wanted you to stay. I wanted you to choose me the way I chose you. I didn't get that, and I'm still learning what to do with the space it left.\n\nI'm not sending this. I'm not sure I even need you to know any of it anymore. I think I just needed to say it somewhere that wasn't just in my head.\n\nSo — I said it. That's enough for today.",
          },
        ],
      },
    ],
    prompts: [
      "Who would this letter be for, if you wrote one today?",
      "What's the one thing you'd never say to their face?",
      "What would you need to feel, in order to let this go?",
    ],
  },
};

export default journalTypesData;